/**
 * Unread Message Tracking Service
 * 
 * Frontend service for managing unread message counts and read receipts
 * Integrates with backend unread tracking API for WhatsApp-style notifications
 * 
 * @version 1.0.0
 * @author CleverAct AI Team
 */

import { api } from './api'
import { logger } from '../utils/logger'

export interface UnreadCountResponse {
  conversation_id: string
  unread_count: number
  last_read_at?: string
  last_read_message_id?: string
}

export interface UnreadSummary {
  total_conversations: number
  conversations_with_unread: number
  total_unread_messages: number
  urgent_conversations: Array<{
    conversation_id: string
    unread_count: number
  }>
  conversation_details: Array<{
    conversation_id: string
    unread_count: number
    last_read_at?: string
  }>
}

class UnreadTrackingService {
  
  /**
   * Get unread count for a specific conversation
   */
  async getUnreadCount(conversationId: string): Promise<number> {
    try {
      const response = await api.get<UnreadCountResponse>(`/api/unread/conversations/${conversationId}/count`)
      return response.data.unread_count
    } catch (error) {
      logger.error('Failed to get unread count', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return 0
    }
  }

  /**
   * Mark messages as read in a conversation
   */
  async markMessagesAsRead(conversationId: string, messageIds?: string[]): Promise<boolean> {
    try {
      logger.info('Marking messages as read', {
        conversationId,
        messageIds: messageIds ? messageIds.length : 'all'
      })

      const response = await api.post(`/api/unread/conversations/${conversationId}/mark-read`, {
        message_ids: messageIds
      })

      if (response.data.success) {
        logger.info('Messages marked as read successfully', {
          conversationId,
          messagesMarked: response.data.messages_marked
        })
        return true
      }

      return false
    } catch (error) {
      logger.error('Failed to mark messages as read', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return false
    }
  }

  /**
   * Mark entire conversation as read (all unread messages)
   */
  async markConversationAsRead(conversationId: string): Promise<boolean> {
    return this.markMessagesAsRead(conversationId) // No messageIds = mark all
  }

  /**
   * Get comprehensive unread summary for agent dashboard
   */
  async getUnreadSummary(): Promise<UnreadSummary | null> {
    try {
      const response = await api.get<UnreadSummary>('/api/unread/summary')
      return response.data
    } catch (error) {
      logger.error('Failed to get unread summary', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return null
    }
  }

  /**
   * Get total unread count across all conversations
   */
  async getTotalUnreadCount(): Promise<number> {
    try {
      const response = await api.get('/api/unread/total')
      return response.data.total_unread_count
    } catch (error) {
      logger.error('Failed to get total unread count', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return 0
    }
  }

  /**
   * Mark all conversations as read
   */
  async markAllConversationsAsRead(): Promise<boolean> {
    try {
      logger.info('Marking all conversations as read')

      const response = await api.post('/api/unread/mark-all-read')

      if (response.data.success) {
        logger.info('All conversations marked as read', {
          totalMarked: response.data.total_messages_marked,
          conversationsAffected: response.data.conversations_affected
        })
        return true
      }

      return false
    } catch (error) {
      logger.error('Failed to mark all conversations as read', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return false
    }
  }

  // Debouncing mechanism for auto-mark-as-read
  private autoMarkTimeouts: Map<string, NodeJS.Timeout> = new Map()

  /**
   * Auto-mark conversation as read when agent opens it
   * This should be called when a conversation is selected/viewed
   * Uses debouncing to prevent multiple rapid calls for the same conversation
   */
  async autoMarkAsReadOnOpen(conversationId: string): Promise<void> {
    try {
      // Clear any existing timeout for this conversation
      const existingTimeout = this.autoMarkTimeouts.get(conversationId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      // Set new timeout with debouncing
      const timeout = setTimeout(async () => {
        await this.markConversationAsRead(conversationId)
        logger.debug('Auto-marked conversation as read on open', { conversationId })
        
        // Remove timeout from map after execution
        this.autoMarkTimeouts.delete(conversationId)
      }, 1000) // 1 second delay
      
      // Store timeout in map
      this.autoMarkTimeouts.set(conversationId, timeout)
      
    } catch (error) {
      logger.error('Failed to auto-mark conversation as read', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  /**
   * Listen for unread count updates via WebSocket
   */
  setupUnreadWebSocketListener(websocket: WebSocket, onUnreadUpdate: (data: any) => void): void {
    const originalOnMessage = websocket.onmessage

    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        
        if (data.type === 'unread_count_update') {
          logger.debug('Received unread count update via WebSocket', data)
          onUnreadUpdate(data)
        }

        // Call original handler if it exists
        if (originalOnMessage) {
          originalOnMessage.call(websocket, event)
        }
      } catch (error) {
        logger.error('Error processing WebSocket message for unread tracking', {
          error: error instanceof Error ? error.message : 'Unknown error'
        })
      }
    }
  }
}

// Export singleton instance
export const unreadTrackingService = new UnreadTrackingService()

// Export hook for React components
export function useUnreadTracking() {
  return {
    getUnreadCount: unreadTrackingService.getUnreadCount.bind(unreadTrackingService),
    markMessagesAsRead: unreadTrackingService.markMessagesAsRead.bind(unreadTrackingService),
    markConversationAsRead: unreadTrackingService.markConversationAsRead.bind(unreadTrackingService),
    getUnreadSummary: unreadTrackingService.getUnreadSummary.bind(unreadTrackingService),
    getTotalUnreadCount: unreadTrackingService.getTotalUnreadCount.bind(unreadTrackingService),
    markAllConversationsAsRead: unreadTrackingService.markAllConversationsAsRead.bind(unreadTrackingService),
    autoMarkAsReadOnOpen: unreadTrackingService.autoMarkAsReadOnOpen.bind(unreadTrackingService)
  }
}